import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { DataView, DataViewLayoutOptions } from "primereact/dataview";
import { Dropdown } from "primereact/dropdown";
import CatalogBreadcrumb from "../components/CatalogBreadcrumb";
import CatalogProductCard from "../components/CatalogProductCard";
import CatalogFiltersSidebar from "../components/CatalogFiltersSidebar";
import CatalogNavigationGrid from "../components/CatalogNavigationGrid";
import { useAuthContext } from "../../../app/providers/contextIndex";
import useCatalogFilters from "../hooks/useCatalogFilters";
import useCatalogProducts from "../hooks/useCatalogProducts";
import { getAtributosFamilia } from "../../../services/backend/catalogService";
import { buildCatalogMenuStructure, buildCatalogProductDetailPath, buildCatalogProductDetailState, CATALOG_SORT_OPTIONS } from "../utils/catalogUtils";
import { buildCatalogPath } from "../../../shared/utils/topbarUtils";
import "../styles/catalog.css";

const SORT_FIELD_MAP = {
  nombre_asc: { sortField: "descripcion", sortOrder: 1 },
  nombre_desc: { sortField: "descripcion", sortOrder: -1 },
  precio_asc: { sortField: "precio", sortOrder: 1 },
  precio_desc: { sortField: "precio", sortOrder: -1 },
};

const Catalog = () => {
    const { isAuthenticated } = useAuthContext();
    const navigate = useNavigate();
    const { dynamicFamilies } = useOutletContext() || {};
    const menuStructure = useMemo(() => buildCatalogMenuStructure(dynamicFamilies), [dynamicFamilies]);

    const {
        collection,
        subcollection,
        searchQuery,
        refreshToken,
        currentFamilyId,
        selectionMode,
        navigationItems,
        shouldShowProducts,
        breadcrumbItems,
    } = useCatalogFilters(dynamicFamilies);
    const [attributeFilters, setAttributeFilters] = useState({});

    const {
        products,
        sortBy,
        setSortBy,
        viewMode,
        setViewMode,
        loading,
        error,
    } = useCatalogProducts({
        currentFamilyId,
        searchQuery,
        refreshToken,
        enabled: shouldShowProducts,
        attributeFilters,
    });

    const buildProductDetailPath = useMemo(
        () => (product) => buildCatalogProductDetailPath({ product }),
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const buildProductDetailState = useMemo(
        () => (product) => buildCatalogProductDetailState({
        product,
        collection,
        subcollection,
        familyId: currentFamilyId,
        }),
        [collection, subcollection, currentFamilyId]
    );
    
    const [priceMin, setPriceMin] = useState("");
    const [priceMax, setPriceMax] = useState("");
    const [onlyInStock, setOnlyInStock] = useState(false);
    const [onlyDiscounted, setOnlyDiscounted] = useState(false);
    const [attributeGroups, setAttributeGroups] = useState([]);
    const [attributesLoading, setAttributesLoading] = useState(false);
    const [attributesError, setAttributesError] = useState("");
    const hasActiveLocalFilters = Boolean(priceMin || priceMax || onlyInStock || onlyDiscounted);
    const hasActiveAttributeFilters = useMemo(() => Object.values(attributeFilters).some((values) => values?.length), [attributeFilters]);
    const hasActiveSidebarFilters = hasActiveLocalFilters || hasActiveAttributeFilters;
    const showDynamicAttributes = shouldShowProducts && currentFamilyId > 0;

    useEffect(() => {
        setAttributeFilters({});
    }, [currentFamilyId]);

    useEffect(() => {
        if (!showDynamicAttributes) {
            setAttributeGroups([]);
            setAttributesLoading(false);
            setAttributesError("");
            return;
        }
        let cancelled = false;
        setAttributesLoading(true);
        setAttributesError("");
        getAtributosFamilia(currentFamilyId)
            .then((response) => {
                if (!cancelled) setAttributeGroups(Array.isArray(response?.atributos) ? response.atributos : []);
            })
            .catch((err) => {
                if (!cancelled) {
                    setAttributeGroups([]);
                    setAttributesError(err?.message || "No fue posible cargar los atributos.");
                }
            })
            .finally(() => {
                if (!cancelled) setAttributesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [currentFamilyId, showDynamicAttributes]);

    const handleAttributeToggle = ({ attributeId, valueId, checked }) => {
        setAttributeFilters((prev) => {
            const nextValues = checked ? [...new Set([...(prev[attributeId] || []), valueId])] : (prev[attributeId] || []).filter((id) => id !== valueId);
            if (!nextValues.length) {
                const { [attributeId]: removed, ...rest } = prev;
                return rest;
            }
            return { ...prev, [attributeId]: nextValues };
        });
    };

    const filteredProducts = useMemo(() => products.filter((product) => {
        const price = Number(product.precio || 0);
        if (priceMin !== "" && price < Number(priceMin)) return false;
        if (priceMax !== "" && price > Number(priceMax)) return false;
        if (onlyInStock && Number(product.stock || 0) <= 0) return false;
        if (onlyDiscounted && Number(product.descuento || 0) <= 0) return false;
        return true;
    }), [onlyDiscounted, onlyInStock, priceMax, priceMin, products]);
    const breadcrumbModel = useMemo(
        () => breadcrumbItems.map((item, index) => ({
            label: item.label,
            to: index === 0 || index === breadcrumbItems.length - 1
                ? ""
                : buildCatalogPath({
                    collection: item.collection,
                    subcollection: item.subcollection,
                    familyId: item.familyId,
                    menuStructure,
                }),
        })),
        [breadcrumbItems, menuStructure]
    );
    const resetSidebarFilters = () => {
        setPriceMin("");
        setPriceMax("");
        setOnlyInStock(false);
        setOnlyDiscounted(false);
        setAttributeFilters({});
    };

    const currentSort = sortBy ? SORT_FIELD_MAP[sortBy] || null : null;

    const dataviewHeader = (
        <div className="catalog-toolbar">
            <div className="catalog-toolbar-left">
                <label htmlFor="catalog-sort">Ordenar por</label>
                <Dropdown
                    inputId="catalog-sort"
                    value={sortBy}
                    options={CATALOG_SORT_OPTIONS}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(event) => setSortBy(event.value)}
                    className="catalog-sort-dropdown"
                />
            </div>
            <div className="catalog-toolbar-right">
                <span>Ver como</span>
                <DataViewLayoutOptions layout={viewMode} onChange={(e) => setViewMode(e.value)} />
            </div>
        </div>
    );

    const productTemplate = (product) => (
        <CatalogProductCard
            product={product}
            isAuthenticated={isAuthenticated}
            onNavigate={navigate}
            buildPath={buildProductDetailPath}
            buildState={buildProductDetailState}
        />
    );

    return (
        <div className="landing-container">
            <div className="catalog-layout">
                <CatalogFiltersSidebar
                    menuStructure={menuStructure}
                    currentCollection={collection}
                    currentSubcollection={subcollection}
                    onNavigate={(path) => navigate(path)}
                    priceMin={priceMin}
                    priceMax={priceMax}
                    onlyInStock={onlyInStock}
                    onlyDiscounted={onlyDiscounted}
                    attributeGroups={attributeGroups}
                    selectedAttributeFilters={attributeFilters}
                    showDynamicAttributes={showDynamicAttributes}
                    attributesLoading={attributesLoading}
                    attributesError={attributesError}
                    hasActiveFilters={hasActiveSidebarFilters}
                    onPriceMinChange={setPriceMin}
                    onPriceMaxChange={setPriceMax}
                    onOnlyInStockChange={setOnlyInStock}
                    onOnlyDiscountedChange={setOnlyDiscounted}
                    onAttributeToggle={handleAttributeToggle}
                    onClear={resetSidebarFilters}
                />

                <div className="catalog-page">
                    <CatalogBreadcrumb
                        items={breadcrumbModel}
                        homeTo="/catalogo"
                        className="catalog-breadcrumb ml-2"
                    />

                    {error ? <div className="catalog-status error">{error}</div> : null}
                    {loading ? <div className="catalog-status">Cargando productos...</div> : null}

                    {!loading && !shouldShowProducts ? (
                        <CatalogNavigationGrid
                            items={navigationItems}
                            collection={collection}
                            currentLevel={selectionMode}
                            menuStructure={menuStructure}
                        />
                    ) : null}

                    {!loading && shouldShowProducts ? (
                        <DataView
                            value={filteredProducts}
                            layout={viewMode}
                            itemTemplate={productTemplate}
                            header={dataviewHeader}
                            sortField={currentSort?.sortField}
                            sortOrder={currentSort?.sortOrder}
                            paginator
                            rows={24}
                            rowsPerPageOptions={[6, 12, 24, 42]}
                            className={viewMode === "grid" ? "catalog-dataview-grid" : "catalog-dataview-list"}
                            emptyMessage={
                                hasActiveSidebarFilters
                                    ? "No se encontraron productos con los filtros seleccionados."
                                    : "No se encontraron productos para esta colección."
                            }
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default Catalog;
